import { Component, Input, OnInit } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import {
  MoodleService,
  MoodleCategory,
  MoodlePushResult,
} from '../../../services/moodle.service';
import {
  LmsRegistryService,
  LmsRegistration,
} from '../../../services/lms-registry.service';

@Component({
  selector: 'app-moodle-push-dialog',
  templateUrl: './moodle-push-dialog.component.html',
  styleUrl: './moodle-push-dialog.component.scss',
})
export class MoodlePushDialogComponent implements OnInit {
  @Input() courseId!: string;
  @Input() courseName!: string;

  categories: MoodleCategory[] = [];
  selectedCategoryId: number | undefined;
  sites: LmsRegistration[] = [];
  selectedRegistrationId: string | undefined;
  targetCourseName = '';
  shortname = '';
  loading = true;
  pushing = false;
  pushResult: MoodlePushResult | null = null;

  constructor(
    protected dialogRef: NbDialogRef<MoodlePushDialogComponent>,
    protected moodleService: MoodleService,
    protected lmsRegistryService: LmsRegistryService,
    protected nbToastrService: NbToastrService
  ) {}

  ngOnInit() {
    this.targetCourseName = this.courseName || '';
    this.loadSites();
  }

  loadSites() {
    this.loading = true;
    this.lmsRegistryService.listMoodleSites().subscribe({
      next: (sites) => {
        this.sites = (sites || []).filter((s) => this.isUsableSite(s));
        const primary = this.sites.find((s) => s.is_primary) || this.sites[0];
        this.selectedRegistrationId = primary?.id;
        this.loadCategories();
      },
      error: () => {
        // Site list is optional — the backend falls back to the primary
        // instance when no registration_id is sent.
        this.sites = [];
        this.selectedRegistrationId = undefined;
        this.loadCategories();
      },
    });
  }

  private isUsableSite(site: LmsRegistration): boolean {
    const status = String(site.connection_status || '').toLowerCase();
    const unusable = ['disconnected', 'paused', 'superseded', 'token_revoked'];
    return !site.disabled_at && !site.ws_token_revoked_at && !unusable.includes(status);
  }

  onSiteChange(registrationId: string) {
    this.selectedRegistrationId = registrationId;
    this.selectedCategoryId = undefined;
    this.loadCategories();
  }

  loadCategories() {
    this.loading = true;
    this.moodleService
      .getMoodleCategories(this.courseId, this.selectedRegistrationId)
      .subscribe({
        next: (cats) => {
          this.categories = cats;
          // Always preselect: Moodle requires a category to create a course.
          if (
            cats.length &&
            !cats.some((c) => c.id === this.selectedCategoryId)
          ) {
            this.selectedCategoryId = cats[0].id;
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.nbToastrService.danger(
            'Moodle-Kategorien konnten nicht geladen werden.',
            'Fehler'
          );
        },
      });
  }

  push() {
    this.pushing = true;
    const moodleCourseName = this.targetCourseName.trim() || this.courseName;
    this.moodleService
      .pushToMoodle(
        this.courseId,
        this.selectedCategoryId,
        this.shortname || undefined,
        moodleCourseName,
        this.selectedRegistrationId
      )
      .subscribe({
        next: (result) => {
          this.pushing = false;
          this.pushResult = result;
          if (result.status === 'partial' || result.warnings?.length) {
            this.nbToastrService.warning(
              result.warnings?.[0] || 'Der Kurs wurde angelegt, aber Inhalte konnten nicht vollständig exportiert werden.',
              'Teilweise exportiert'
            );
          } else {
            this.nbToastrService.success(
              `"${moodleCourseName}" wurde zu Moodle gepusht.`,
              'Erfolgreich'
            );
          }
        },
        error: (err) => {
          this.pushing = false;
          const msg = err?.error?.detail || 'Push zu Moodle fehlgeschlagen.';
          this.nbToastrService.danger(msg, 'Fehler');
        },
      });
  }

  close() {
    this.dialogRef.close(this.pushResult !== null);
  }
}
